import React, { useEffect, useState } from "react";
import {
  Container,
  Paper,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Chip,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Card,
  CardContent,
  Grid,
} from "@mui/material";
import { Sms, Group, Person, CheckCircle, Cancel } from "@mui/icons-material";

const AdminMessagesPage = () => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [typeFilter, setTypeFilter] = useState("all");
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [openDetailDialog, setOpenDetailDialog] = useState(false);
  const [stats, setStats] = useState(null);

  const [toast, setToast] = useState({
    show: false,
    message: "",
    type: "success",
  });

  useEffect(() => {
    fetchMessages();
    fetchStats();
  }, [typeFilter]);

  const fetchMessages = async () => {
    try {
      setLoading(true);
      const url =
        typeFilter === "all"
          ? "https://api.dilanaslan.com/api/api"
          : `https://api.dilanaslan.com/api/messages?type=${typeFilter}`;

      const response = await fetch(url);
      const result = await response.json();

      if (result.success) {
        setMessages(result.data);
      }
    } catch (error) {
      console.error("Mesajlar yüklenirken hata:", error);
      showToast("Mesajlar yüklenirken hata oluştu", "error");
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await fetch(
        "https://api.dilanaslan.com/api/messages/stats/summary"
      );
      const result = await response.json();

      if (result.success) {
        setStats(result.data);
      }
    } catch (error) {
      console.error("İstatistikler yüklenirken hata:", error);
    }
  };

  const showToast = (message, type = "success") => {
    setToast({ show: true, message, type });
  };

  const handleViewDetails = async (messageId) => {
    try {
      const response = await fetch(
        `https://api.dilanaslan.com/api/messages/${messageId}`
      );
      const result = await response.json();

      if (result.success) {
        setSelectedMessage(result.data);
        setOpenDetailDialog(true);
      }
    } catch (error) {
      console.error("Mesaj detayı yüklenirken hata:", error);
      showToast("Mesaj detayı yüklenirken hata oluştu", "error");
    }
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleString("tr-TR", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "completed":
        return "success";
      case "failed":
        return "error";
      case "processing":
        return "warning";
      default:
        return "default";
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case "completed":
        return "Tamamlandı";
      case "failed":
        return "Başarısız";
      case "processing":
        return "İşleniyor";
      case "pending":
        return "Bekliyor";
      default:
        return status;
    }
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h4" gutterBottom>
        <Sms sx={{ mr: 1, verticalAlign: "middle" }} />
        Mesaj Geçmişi
      </Typography>

      {/* İstatistik Kartları */}
      {stats && (
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Toplam Mesaj
                </Typography>
                <Typography variant="h4">{stats.messages.total}</Typography>
                <Typography variant="caption" color="textSecondary">
                  Tek: {stats.messages.single} | Toplu: {stats.messages.bulk}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Başarılı
                </Typography>
                <Typography variant="h4" color="success.main">
                  {stats.messages.success}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Başarısız
                </Typography>
                <Typography variant="h4" color="error.main">
                  {stats.messages.failed}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Toplam Alıcı
                </Typography>
                <Typography variant="h4">{stats.recipients.total}</Typography>
                <Typography variant="caption" color="success.main">
                  Gönderilen: {stats.recipients.success}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Filtreler */}
      <Box sx={{ mb: 3, display: "flex", gap: 2, alignItems: "center" }}>
        <FormControl sx={{ minWidth: 200 }}>
          <InputLabel>Mesaj Tipi</InputLabel>
          <Select
            value={typeFilter}
            label="Mesaj Tipi"
            onChange={(e) => setTypeFilter(e.target.value)}
          >
            <MenuItem value="all">Tümü</MenuItem>
            <MenuItem value="single">Tek Mesaj</MenuItem>
            <MenuItem value="bulk">Toplu Mesaj</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {/* Mesaj Tablosu */}
      <Paper>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Tip</TableCell>
              <TableCell>Mesaj</TableCell>
              <TableCell>Alıcı Sayısı</TableCell>
              <TableCell>Başarılı/Başarısız</TableCell>
              <TableCell>Durum</TableCell>
              <TableCell>Tarih</TableCell>
              <TableCell>İşlem</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  Yükleniyor...
                </TableCell>
              </TableRow>
            ) : messages.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  Mesaj bulunamadı
                </TableCell>
              </TableRow>
            ) : (
              messages.map((message) => (
                <TableRow key={message._id} hover>
                  <TableCell>
                    <Chip
                      icon={message.type === "bulk" ? <Group /> : <Person />}
                      label={message.type === "bulk" ? "Toplu" : "Tek"}
                      size="small"
                      color={message.type === "bulk" ? "primary" : "default"}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography
                      variant="body2"
                      sx={{
                        maxWidth: 300,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {message.message}
                    </Typography>
                  </TableCell>
                  <TableCell>{message.totalRecipients}</TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      <CheckCircle
                        sx={{ fontSize: 16, color: "success.main", mr: 0.5 }}
                      />
                      {message.successCount}
                      {" / "}
                      <Cancel
                        sx={{ fontSize: 16, color: "error.main", mr: 0.5 }}
                      />
                      {message.failedCount}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={getStatusText(message.status)}
                      color={getStatusColor(message.status)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>{formatDate(message.createdAt)}</TableCell>
                  <TableCell>
                    <Button
                      size="small"
                      onClick={() => handleViewDetails(message._id)}
                    >
                      Detay
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Paper>

      {/* Detay Dialog */}
      <Dialog
        open={openDetailDialog}
        onClose={() => setOpenDetailDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Mesaj Detayı</DialogTitle>
        <DialogContent>
          {selectedMessage && (
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Mesaj İçeriği:
              </Typography>
              <Paper sx={{ p: 2, mb: 2, bgcolor: "grey.100" }}>
                <Typography>{selectedMessage.message}</Typography>
              </Paper>

              <Typography variant="subtitle2" gutterBottom>
                Bilgiler:
              </Typography>
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={6}>
                  <Typography variant="body2">
                    <strong>Tip:</strong>{" "}
                    {selectedMessage.type === "bulk" ? "Toplu" : "Tek"}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2">
                    <strong>Durum:</strong>{" "}
                    {getStatusText(selectedMessage.status)}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2">
                    <strong>Toplam Alıcı:</strong>{" "}
                    {selectedMessage.totalRecipients}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2">
                    <strong>Başarılı:</strong> {selectedMessage.successCount}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2">
                    <strong>Başarısız:</strong> {selectedMessage.failedCount}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2">
                    <strong>Tarih:</strong>{" "}
                    {formatDate(selectedMessage.createdAt)}
                  </Typography>
                </Grid>
              </Grid>

              <Typography variant="subtitle2" gutterBottom>
                Alıcılar:
              </Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>İsim</TableCell>
                    <TableCell>Telefon</TableCell>
                    <TableCell>Durum</TableCell>
                    <TableCell>Gönderim Zamanı</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {selectedMessage.recipients.map((recipient, index) => (
                    <TableRow key={index}>
                      <TableCell>{recipient.customerName}</TableCell>
                      <TableCell>{recipient.phone}</TableCell>
                      <TableCell>
                        <Chip
                          label={
                            recipient.status === "sent"
                              ? "Gönderildi"
                              : "Başarısız"
                          }
                          color={
                            recipient.status === "sent" ? "success" : "error"
                          }
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        {recipient.sentAt ? formatDate(recipient.sentAt) : "-"}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDetailDialog(false)}>Kapat</Button>
        </DialogActions>
      </Dialog>

      {/* Toast */}
      <Snackbar
        open={toast.show}
        autoHideDuration={3000}
        onClose={() => setToast({ ...toast, show: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity={toast.type}>{toast.message}</Alert>
      </Snackbar>
    </Container>
  );
};

export default AdminMessagesPage;
